// The app's languages. The English text in the code is the key:
//   t("Go Live")                          → "Go Live", or its translation
//   t("Hi {name}", { name })              → placeholders in braces
//   tn("{count} viewer", "{count} viewers", count)   → plurals
//   tc("status", "Live")                  → the same English, a different meaning
//   tRich("Read our <terms>Terms</terms>", { terms: (text) => <Link>{text}</Link> })
// Spanish lives in localization/es (Mexico and the rest of Latin America);
// localization/es-ES only holds what Spain says differently. Portuguese lives in
// localization/pt (Brazil); localization/pt-PT only holds what Portugal says
// differently. German, French, Dutch, Polish and Japanese have one folder each
// (de, fr, nl, pl, ja). Anything without a translation shows the English. See
// localization/README.md.
//
// The language is fixed for the life of the JavaScript: index.js waits for
// it before loading the app, so t() can run at the top of a module. Changing
// it saves the choice and reloads the app.

import React from "react";
import { Platform } from "react-native";
import moment from "moment";
import "moment/locale/de";
import "moment/locale/es";
import "moment/locale/es-mx";
import "moment/locale/fr";
import "moment/locale/ja";
import "moment/locale/nl";
import "moment/locale/pl";
import "moment/locale/pt";
import "moment/locale/pt-br";

import { de, es, esES, fr, ja, nl, pl, pt, ptPT } from "./catalog";
import {
  readInitialPreference,
  loadStoredPreference,
  storePreference,
  reloadApp,
} from "./device";

// In the picker's order: each language's own name, alphabetically.
export const LANGUAGES = [
  "en",
  "de",
  "es-MX",
  "es-ES",
  "fr",
  "nl",
  "pl",
  "pt-BR",
  "pt-PT",
  "ja",
];

// Each language in its own words, so anyone can find theirs.
const LANGUAGE_NAMES = {
  en: "English",
  de: "Deutsch",
  "es-MX": "Español (México)",
  "es-ES": "Español (España)",
  fr: "Français",
  nl: "Nederlands",
  pl: "Polski",
  "pt-BR": "Português (Brasil)",
  "pt-PT": "Português (Portugal)",
  ja: "日本語",
};

// Where each language's text comes from, first match wins: Spain's (or
// Portugal's) differences, then the Mexico and Latin America (or Brazil) catalog.
const CATALOGS = {
  en: [],
  de: [de],
  "es-MX": [es],
  "es-ES": [esES, es],
  fr: [fr],
  nl: [nl],
  pl: [pl],
  "pt-BR": [pt],
  "pt-PT": [ptPT, pt],
  ja: [ja],
};

const MOMENT_LOCALES = {
  en: "en",
  de: "de",
  "es-MX": "es-mx",
  "es-ES": "es",
  fr: "fr",
  nl: "nl",
  pl: "pl",
  "pt-BR": "pt-br",
  "pt-PT": "pt",
  ja: "ja",
};

// Languages with one catalog, whatever the region: "de-AT", "fr-CA", "nl-BE".
const ONE_VARIANT = { de: "de", fr: "fr", nl: "nl", pl: "pl", ja: "ja" };

let preference = "auto";
let language = "en";
let ready = false;
let readyPromise = null;

// "es-AR", "es_419", "es-US" → "es-MX"; "es-ES" → "es-ES"; "en-GB" → "en";
// "de-CH" → "de"; "pt-BR" and a bare "pt" → "pt-BR"; Portuguese from anywhere
// else ("pt-PT", "pt-AO", "pt-MZ") writes as Portugal does → "pt-PT".
export function languageFromTag(tag) {
  const parts = String(tag || "")
    .replace(/_/g, "-")
    .split("-");
  const code = parts[0].toLowerCase();
  if (code === "en") return "en";
  if (ONE_VARIANT[code]) return ONE_VARIANT[code];
  if (code !== "es" && code !== "pt") return null;
  const region = parts
    .slice(1)
    .find((part) => /^([A-Za-z]{2}|\d{3})$/.test(part));
  if (code === "pt")
    return !region || region.toUpperCase() === "BR" ? "pt-BR" : "pt-PT";
  return region && region.toUpperCase() === "ES" ? "es-ES" : "es-MX";
}

// The first language in the device's or browser's list that the app speaks.
export function detectLanguage(locales) {
  let list = locales;
  if (!list) {
    try {
      // Required here so tests can import this file without native modules.
      list = require("expo-localization").getLocales();
    } catch (error) {
      list = [];
    }
  }
  for (const locale of list || []) {
    const found =
      languageFromTag(locale?.languageTag) ||
      languageFromTag(
        locale?.languageCode &&
          [locale.languageCode, locale.regionCode].filter(Boolean).join("-")
      );
    if (found) return found;
  }
  return "en";
}

function normalizePreference(value) {
  if (!value || value === "auto") return "auto";
  if (LANGUAGES.includes(value)) return value;
  return languageFromTag(value) || "auto";
}

function apply(nextPreference) {
  preference = normalizePreference(nextPreference);
  language = preference === "auto" ? detectLanguage() : preference;
  // Loading a moment locale also selects it, so always set it back here.
  moment.locale(MOMENT_LOCALES[language] || "en");
  if (Platform.OS === "web" && typeof document !== "undefined") {
    document.documentElement.lang = language;
  }
}

// Tests always run in English unless they choose a language.
apply(
  typeof process !== "undefined" && process.env?.JEST_WORKER_ID
    ? "en"
    : readInitialPreference()
);

// Native apps keep the choice in AsyncStorage, which can't be read
// synchronously; the web knows it already.
export function whenLanguageReady() {
  if (ready) return Promise.resolve(language);
  if (!readyPromise) {
    readyPromise = loadStoredPreference()
      .then((stored) => {
        if (stored) apply(stored);
      })
      .catch(() => {})
      .then(() => {
        ready = true;
        return language;
      });
  }
  return readyPromise;
}

export function isLanguageReady() {
  return ready || Platform.OS === "web";
}

// One of LANGUAGES: "en", "es-MX", "de", "pt-BR"…
export function getLanguage() {
  return language;
}

// The locale numbers and amounts are written in: "1,234.5" for English, as
// the app always had, "1.234,5" in German, "1 234,5" in French.
const NUMBER_LOCALES = {
  en: "en-US",
  de: "de-DE",
  "es-MX": "es-MX",
  "es-ES": "es-ES",
  fr: "fr-FR",
  nl: "nl-NL",
  pl: "pl-PL",
  "pt-BR": "pt-BR",
  "pt-PT": "pt-PT",
  ja: "ja-JP",
};

export function numberLocale() {
  return NUMBER_LOCALES[language] || "en-US";
}

// The device's or browser's first language as it reports it ("es-AR",
// "fr-FR"), whatever the app shows; null if unknown.
export function deviceLanguage() {
  try {
    return require("expo-localization").getLocales()?.[0]?.languageTag || null;
  } catch (error) {
    return null;
  }
}

// English shows the text as written; other languages may need their own
// version of something English gets for free (a picker's locale, a message
// from a library).
export function isEnglish() {
  return language === "en";
}

// For guest pages the user opens to look at (embed and Video Wall previews):
// evt.live reads ?lang= ("es-MX", "de", "pt-PT"…; its Brazilian catalog is
// "pt"). Null in English, so those pages follow the browser as before. Never
// for links people share: guests get their own language.
export function previewLanguage() {
  if (language === "en") return null;
  return language === "pt-BR" ? "pt" : language;
}

// "auto" or one of LANGUAGES.
export function getLanguagePreference() {
  return preference;
}

export function languageName(value) {
  if (value === "auto")
    return t("Automatic ({language})", {
      language: LANGUAGE_NAMES[detectLanguage()],
    });
  return LANGUAGE_NAMES[value] || value;
}

// For the language picker: Automatic first, then each language.
export function languageOptions() {
  return ["auto", ...LANGUAGES].map((value) => ({
    value,
    label: languageName(value),
  }));
}

// Saves the choice and, if the language changes, reloads the app in it.
export async function setLanguagePreference(value) {
  const next = normalizePreference(value);
  const before = language;
  await storePreference(next);
  apply(next);
  if (language !== before) await reloadApp();
}

function lookup(key) {
  for (const catalog of CATALOGS[language] || []) {
    if (catalog[key] !== undefined) return catalog[key];
  }
  return undefined;
}

function interpolate(text, vars) {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (match, name) =>
    vars[name] === undefined || vars[name] === null ? match : String(vars[name])
  );
}

export function t(text, vars) {
  const found = lookup(text);
  return interpolate(typeof found === "string" ? found : text, vars);
}

// A noun inside a sentence ("Anna is now editor"): lowercase, except in German,
// which capitalizes nouns ("Anna ist jetzt Bearbeiter").
export function nounInSentence(text) {
  return language === "de" ? text : text.toLowerCase();
}

// The same English with a different meaning gets a context:
// tc("status", "Live") reads "status|Live" from the catalog.
export function tc(context, text, vars) {
  let found = lookup(`${context}|${text}`);
  if (typeof found !== "string") found = lookup(text);
  return interpolate(typeof found === "string" ? found : text, vars);
}

// The plural form a count takes (CLDR's rules for whole numbers). English,
// Spanish, German, Dutch and Portuguese use the singular for exactly 1
// ("1 evento", "0 eventos"); French for 0 and 1 too ("0 événement"); Polish
// has one, few (2–4, 22–24…) and many (0, 5–21…); Japanese one form.
export function pluralForm(count, lang = language) {
  const n = Math.abs(Number(count));
  switch (lang) {
    case "ja":
      return "other";
    case "fr":
      return n < 2 ? "one" : "other";
    case "pl": {
      if (!Number.isInteger(n)) return "other";
      if (n === 1) return "one";
      const ten = n % 10;
      const hundred = n % 100;
      if (ten >= 2 && ten <= 4 && (hundred < 12 || hundred > 14)) return "few";
      return "many";
    }
    default:
      return n === 1 ? "one" : "other";
  }
}

// {count} is always available.
export function tn(one, other, count, vars) {
  const found = lookup(one);
  const n = Number(count);
  let text;
  if (found && typeof found === "object") {
    text = found[pluralForm(n)] ?? found.other ?? found.many;
  } else if (typeof found === "string") {
    text = found;
  } else {
    text = n === 1 ? one : other;
  }
  return interpolate(text, { count, ...vars });
}

function keyed(node, key) {
  return React.isValidElement(node) && node.key == null
    ? React.cloneElement(node, { key })
    : node;
}

// Text with styled or linked parts, for <Text> children. Tags wrap the words a
// renderer gets; braces take a value or an element:
//   tRich("By continuing you agree to our <terms>Terms</terms>.", {
//     terms: (words) => <Text onPress={openTerms}>{words}</Text>,
//   })
// Returns an array of strings and elements.
export function tRich(text, parts = {}) {
  const found = lookup(text);
  const source = typeof found === "string" ? found : text;
  const out = [];
  const pattern = /<(\w+)>([\s\S]*?)<\/\1>|\{(\w+)\}/g;
  let last = 0;
  let index = 0;
  let match;
  while ((match = pattern.exec(source))) {
    if (match.index > last) out.push(source.slice(last, match.index));
    if (match[1]) {
      const render = parts[match[1]];
      const words = interpolate(match[2], parts);
      out.push(
        typeof render === "function" ? keyed(render(words), index++) : words
      );
    } else {
      const value = parts[match[3]];
      out.push(
        value === undefined || value === null
          ? match[0]
          : typeof value === "function"
          ? keyed(value(), index++)
          : keyed(value, index++)
      );
    }
    last = pattern.lastIndex;
  }
  if (last < source.length) out.push(source.slice(last));
  return out;
}

// Only for tests: run code in a language without saving it.
export function __setLanguageForTests(value) {
  apply(value);
}
